import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Container, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Button,
  CircularProgress,
  TextField,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Autocomplete
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { format, startOfDay, endOfDay } from 'date-fns';

import { Tabs, Tab } from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/layout/PageHeader';
import ReportService from '../../services/report';
import AuthService from '../../services/auth';

interface ReportData {
  data: any[];
  daily?: any[]; // キャスト別の日次推移、またはドライバー出勤の日別オン時間
  history?: { action: string; timestamp: string; date: string; time: string }[]; // ドライバー出勤のオン/オフ履歴
  drivers?: { driver_id: number; driver_name: string }[]; // ドライバー一覧
  summary?: {
    total_revenue?: number;
    total_grand_total?: number;
    total_orders?: number;
    average_order_value?: number;
    cast_revenue?: number;
    store_revenue?: number;
    total_castback?: number;
    options?: { name: string; count: number }[];
    options_total_count?: number;
    hourly?: { hour: string; hour_value: number; grand_total: number; orders: number }[]; // 時間別（入室時間ベース）
    total_hours?: number; // ドライバー出勤の合計オン時間
    total_days?: number;  // ドライバー出勤の稼働日数
  };
  [key: string]: any;
}

// バックエンドの実装に合わせてレポートタイプを定義
type ReportType = 'sales' | 'cast' | 'driver_attendance';

const Reports = () => {
  const [reportType, setReportType] = useState<ReportType>('sales');
  const [groupBy, setGroupBy] = useState('day');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  // 表示タブ（0=表, 1以降=各グラフ）。レポート種別ごとに意味が変わる。
  const [chartTab, setChartTab] = useState(0);

  // キャスト別「日次売上折れ線」で個別キャストを選択するための状態
  const [selectedCast, setSelectedCast] = useState<{ cast_id: number; cast_name: string } | null>(null);
  const [castDailyData, setCastDailyData] = useState<{ date: string; castback: number; orders: number }[] | null>(null);
  const [castDailyLoading, setCastDailyLoading] = useState(false);
  const [castDailyError, setCastDailyError] = useState<string | null>(null);

  // ドライバー出勤履歴：ドライバー一覧・選択・出勤データ
  const [driverOptions, setDriverOptions] = useState<{ driver_id: number; driver_name: string }[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<{ driver_id: number; driver_name: string } | null>(null);
  const [driverAttendance, setDriverAttendance] = useState<ReportData | null>(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState<string | null>(null);

  // キャスト別詳細モーダル
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<any | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const openCastDetail = async (castId: number) => {
    setDetailOpen(true);
    setDetailData(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const formattedStartDate = format(startOfDay(startDate), 'yyyy-MM-dd');
      const formattedEndDate = format(endOfDay(endDate), 'yyyy-MM-dd');
      const data = await ReportService.getCastDetail(castId, formattedStartDate, formattedEndDate);
      setDetailData(data);
    } catch (err) {
      console.error('キャスト明細取得エラー:', err);
      setDetailError('明細の取得に失敗しました');
    } finally {
      setDetailLoading(false);
    }
  };

  // キャスト別「日次売上折れ線」用：選択キャストの日次推移を取得
  const handleSelectCast = async (cast: { cast_id: number; cast_name: string } | null) => {
    setSelectedCast(cast);
    setCastDailyData(null);
    setCastDailyError(null);
    if (!cast) return; // 選択解除時は全体表示に戻す
    setCastDailyLoading(true);
    try {
      const formattedStartDate = format(startOfDay(startDate), 'yyyy-MM-dd');
      const formattedEndDate = format(endOfDay(endDate), 'yyyy-MM-dd');
      const data = await ReportService.getCastDetail(cast.cast_id, formattedStartDate, formattedEndDate);
      // cast-detail の days[] を日次折れ線用に整形
      const daily = (data?.days || []).map((d: any) => ({
        date: d.date,
        castback: Number(d.total_castback || 0),
        orders: Number(d.order_count || 0),
      }));
      setCastDailyData(daily);
    } catch (err) {
      console.error('キャスト日次推移取得エラー:', err);
      setCastDailyError('日次推移の取得に失敗しました');
    } finally {
      setCastDailyLoading(false);
    }
  };

  // ドライバー出勤履歴：選択ドライバーの履歴＋日別オン時間を取得
  const handleSelectDriver = async (driver: { driver_id: number; driver_name: string } | null) => {
    setSelectedDriver(driver);
    setDriverAttendance(null);
    setAttendanceError(null);
    if (!driver) return;
    setAttendanceLoading(true);
    try {
      const formattedStartDate = format(startOfDay(startDate), 'yyyy-MM-dd');
      const formattedEndDate = format(endOfDay(endDate), 'yyyy-MM-dd');
      const data = await ReportService.getDriverAttendance(driver.driver_id, formattedStartDate, formattedEndDate);
      setDriverAttendance(data);
    } catch (err) {
      console.error('ドライバー出勤履歴取得エラー:', err);
      setAttendanceError('出勤履歴の取得に失敗しました');
    } finally {
      setAttendanceLoading(false);
    }
  };

  // モーダル内容を別ウィンドウで印刷する
  const handlePrintDetail = () => {
    if (!detailData) return;
    const win = window.open('', '_blank', 'width=800,height=900');
    if (!win) return;

    const rows = (detailData.days || [])
      .map((day: any) => {
        const orderRows = (day.orders || [])
          .map((o: any) => {
            const optText =
              o.options && o.options.length > 0
                ? '（' +
                  o.options
                    .map((op: any) => `${op.name} ${Number(op.price || 0).toLocaleString()}円`)
                    .join('、') +
                  '）'
                : '';
            return `<tr>
              <td>${o.time || ''}</td>
              <td>${o.course_label || ''} ${Number(o.course_price || 0).toLocaleString()}円 ${optText}</td>
              <td style="text-align:right">${Number(o.castback || 0).toLocaleString()}円</td>
            </tr>`;
          })
          .join('');
        return `
          <tr class="date-row"><td colspan="3">${day.date}（合計バック: ${Number(day.total_castback || 0).toLocaleString()}円 / ${day.order_count}件）</td></tr>
          ${orderRows}
        `;
      })
      .join('');

    win.document.write(`
      <html>
        <head>
          <title>キャスト別明細 - ${detailData.cast_name || ''}</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            h2 { margin-bottom: 4px; }
            .meta { color: #555; margin-bottom: 16px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ccc; padding: 6px 8px; font-size: 13px; }
            th { background: #f5f5f5; }
            .date-row td { background: #eef3ff; font-weight: bold; }
            .summary { margin-top: 16px; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>キャスト別明細：${detailData.cast_name || ''}</h2>
          <div class="meta">期間：${detailData.start_date} ～ ${detailData.end_date}</div>
          <table>
            <thead>
              <tr><th>時間</th><th>内容</th><th>キャストバック</th></tr>
            </thead>
            <tbody>${rows || '<tr><td colspan="3">データがありません</td></tr>'}</tbody>
          </table>
          <div class="summary">期間合計バック：${Number(detailData.summary?.total_castback || 0).toLocaleString()}円 / 完了オーダー ${detailData.summary?.total_orders || 0}件</div>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  const handleReportTypeChange = (event: SelectChangeEvent) => {
    setReportType(event.target.value as ReportType);
  };

  const handleGroupByChange = (event: SelectChangeEvent) => {
    setGroupBy(event.target.value);
  };

  const fetchReportData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const formattedStartDate = format(startOfDay(startDate), 'yyyy-MM-dd');
      const formattedEndDate = format(endOfDay(endDate), 'yyyy-MM-dd');

      let data;
      switch (reportType) {
        case 'sales':
          data = await ReportService.getSalesReport(formattedStartDate, formattedEndDate, groupBy);
          break;
        case 'cast':
          data = await ReportService.getCastPerformance(formattedStartDate, formattedEndDate);
          break;
        case 'driver_attendance': {
          // ドライバー一覧を取得して候補にする（実データは選択後に取得）
          const res = await ReportService.getDrivers();
          const drivers = res?.drivers || [];
          setDriverOptions(drivers);
          data = { data: [], drivers };
          break;
        }
        default:
          data = await ReportService.getSalesReport(formattedStartDate, formattedEndDate, groupBy);
      }

      setReportData(data);
      setChartTab(0); // 新しいレポート生成時は表タブに戻す
      // キャスト個別選択状態をリセット（期間が変わると実績キャストも変わるため）
      setSelectedCast(null);
      setCastDailyData(null);
      setCastDailyError(null);
      // ドライバー出勤選択もリセット
      setSelectedDriver(null);
      setDriverAttendance(null);
      setAttendanceError(null);
      setIsLoading(false);
    } catch (err) {
      console.error('レポート生成エラー:', err);
      setError('レポートの生成に失敗しました');
      setIsLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      setIsLoading(true);
      const formattedStartDate = format(startOfDay(startDate), 'yyyy-MM-dd');
      const formattedEndDate = format(endOfDay(endDate), 'yyyy-MM-dd');

      const reportId = await ReportService.generateReport({
        type: reportType,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        group_by: groupBy
      });
      
      if (reportId) {
        await ReportService.downloadReport(reportId.id || '');
      }

      setIsLoading(false);
    } catch (err) {
      console.error('レポートダウンロードエラー:', err);
      setError('レポートのダウンロードに失敗しました');
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                レポート設定
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>レポート種別</InputLabel>
                    <Select
                      value={reportType}
                      onChange={handleReportTypeChange}
                      label="レポート種別"
                    >
                      <MenuItem value="sales">売上レポート</MenuItem>
                      <MenuItem value="cast">キャスト別レポート</MenuItem>
                      <MenuItem value="driver_attendance">ドライバー出勤履歴</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>集計単位</InputLabel>
                    <Select
                      value={groupBy}
                      onChange={handleGroupByChange}
                      label="集計単位"
                    >
                      <MenuItem value="day">日次</MenuItem>
                      <MenuItem value="week">週次</MenuItem>
                      <MenuItem value="month">月次</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                    <DatePicker
                      label="開始日"
                      value={startDate}
                      onChange={(newValue) => newValue && setStartDate(newValue)}
                      slotProps={{
                        textField: { fullWidth: true }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                    <DatePicker
                      label="終了日"
                      value={endDate}
                      onChange={(newValue) => newValue && setEndDate(newValue)}
                      slotProps={{
                        textField: { fullWidth: true }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={fetchReportData}
                  disabled={isLoading}
                >
                  レポート生成
                </Button>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  onClick={handleDownloadReport}
                  disabled={isLoading}
                >
                  レポートダウンロード
                </Button>
              </Box>
            </Paper>
          </Grid>

          {isLoading && (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Grid>
          )}

          {error && (
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: 3, mt: 2, bgcolor: 'error.light' }}>
                <Typography color="error">{error}</Typography>
              </Paper>
            </Grid>
          )}

          {reportData && !isLoading && (
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedStaffId ? 'スタッフ個別レポート' : 'レポート詳細'}
                </Typography>
                {renderReportTable()}
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>
    );
  };

  // グラフ用の数値フォーマッタ（Tooltip / 軸）
  const yen = (v: number | string) => `${Number(v || 0).toLocaleString()}円`;

  // 売上レポートのタブ内容を出し分ける
  const renderSalesTab = () => {
    // 1: 日別折れ線
    if (chartTab === 1) {
      const data = reportData?.data || [];
      if (data.length === 0) return <Typography>データがありません</Typography>;
      return (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis yAxisId="amount" tickFormatter={(v) => Number(v).toLocaleString()} />
            <YAxis yAxisId="orders" orientation="right" allowDecimals={false} />
            <RechartsTooltip formatter={(v: any, name: any) => (name === '注文数' ? `${Number(v || 0).toLocaleString()}件` : yen(v))} />
            <Legend />
            <Line yAxisId="amount" type="monotone" dataKey="grand_total" name="総額" stroke="#1976d2" strokeWidth={2} />
            <Line yAxisId="amount" type="monotone" dataKey="cast_revenue" name="キャスト収益" stroke="#e91e63" strokeWidth={2} />
            <Line yAxisId="amount" type="monotone" dataKey="store_revenue" name="店舗収益" stroke="#2e7d32" strokeWidth={2} />
            <Line yAxisId="orders" type="monotone" dataKey="orders" name="注文数" stroke="#ff9800" strokeWidth={2} strokeDasharray="5 3" />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    // 2: 時間別折れ線（入室時間ベース）
    if (chartTab === 2) {
      const hourly = reportData?.summary?.hourly || [];
      if (hourly.length === 0) return <Typography>データがありません</Typography>;
      return (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={hourly} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="hour" />
            <YAxis yAxisId="amount" tickFormatter={(v) => Number(v).toLocaleString()} />
            <YAxis yAxisId="orders" orientation="right" allowDecimals={false} />
            <RechartsTooltip formatter={(v: any, name: any) => (name === '注文数' ? `${Number(v || 0).toLocaleString()}件` : yen(v))} />
            <Legend />
            <Line yAxisId="amount" type="monotone" dataKey="grand_total" name="総額" stroke="#1976d2" strokeWidth={2} />
            <Line yAxisId="orders" type="monotone" dataKey="orders" name="注文数" stroke="#ff9800" strokeWidth={2} strokeDasharray="5 3" />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    // 3: オプション数棒グラフ
    if (chartTab === 3) {
      const options = reportData?.summary?.options || [];
      if (options.length === 0) return <Typography>データがありません</Typography>;
      return (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={options} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" interval={0} angle={-20} textAnchor="end" height={60} />
            <YAxis allowDecimals={false} />
            <RechartsTooltip formatter={(v: any) => `${Number(v || 0).toLocaleString()}件`} />
            <Legend />
            <Bar dataKey="count" name="件数" fill="#1976d2" />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    // 0: 表
    return renderSalesTable();
  };

  // キャスト別レポートのタブ内容を出し分ける
  const renderCastTab = () => {
    // 1: 日次売上（キャストバック）折れ線
    if (chartTab === 1) {
      // キャスト候補（期間内に実績のあるキャスト一覧）
      const castOptions: { cast_id: number; cast_name: string }[] = (reportData?.data || []).map(
        (c: any) => ({ cast_id: c.cast_id, cast_name: c.cast_name })
      );

      // 表示するデータ：キャストを選択していればそのキャストの日次、未選択なら全体
      const daily = selectedCast ? castDailyData || [] : reportData?.daily || [];

      return (
        <Box>
          <Autocomplete
            size="small"
            sx={{ maxWidth: 360, mb: 2 }}
            options={castOptions}
            value={selectedCast}
            onChange={(_, val) => handleSelectCast(val)}
            getOptionLabel={(opt) => opt.cast_name || ''}
            isOptionEqualToValue={(opt, val) => opt.cast_id === val.cast_id}
            renderInput={(params) => (
              <TextField {...params} label="キャストを検索・選択（未選択は全体）" />
            )}
            noOptionsText="該当するキャストがいません"
          />

          {castDailyLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}
          {castDailyError && <Typography color="error">{castDailyError}</Typography>}

          {!castDailyLoading && !castDailyError && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {selectedCast ? `${selectedCast.cast_name} の日次推移` : '全キャスト合計の日次推移'}
              </Typography>
              {daily.length === 0 ? (
                <Typography>データがありません</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={daily} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis yAxisId="amount" tickFormatter={(v) => Number(v).toLocaleString()} />
                    <YAxis yAxisId="orders" orientation="right" allowDecimals={false} />
                    <RechartsTooltip
                      formatter={(v: any, name: any) =>
                        name === '注文数' ? `${Number(v || 0).toLocaleString()}件` : yen(v)
                      }
                    />
                    <Legend />
                    <Line yAxisId="amount" type="monotone" dataKey="castback" name="キャストバック" stroke="#e91e63" strokeWidth={2} />
                    <Line yAxisId="orders" type="monotone" dataKey="orders" name="注文数" stroke="#ff9800" strokeWidth={2} strokeDasharray="5 3" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </>
          )}
        </Box>
      );
    }

    // 0: 表
    return renderCastTable();
  };

  const renderReportTable = () => {
    // レポートタイプに基づいて適切なテーブル／グラフをレンダリング
    if (!reportData) return null;

    // レポート種別ごとのタブ定義
    const tabsByType: Record<ReportType, string[]> = {
      sales: ['表', '日別折れ線', '時間別折れ線', 'オプション数棒グラフ'],
      cast: ['表', '日次売上折れ線'],
      driver_attendance: ['履歴', '日別オン時間'],
    };
    const tabLabels = tabsByType[reportType] || ['表'];

    const renderActiveTab = () => {
      if (reportType === 'sales') return renderSalesTab();
      if (reportType === 'cast') return renderCastTab();
      if (reportType === 'driver_attendance') return renderDriverAttendanceTab();
      return null;
    };

    return (
      <Box>
        <Tabs
          value={chartTab}
          onChange={(_, v) => setChartTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          {tabLabels.map((label, i) => (
            <Tab key={i} label={label} />
          ))}
        </Tabs>
        {renderActiveTab()}
      </Box>
    );
  };

  // ドライバー出勤履歴タブ（0=履歴, 1=日別オン時間）
  const renderDriverAttendanceTab = () => {
    const picker = (
      <Autocomplete
        size="small"
        sx={{ maxWidth: 360, mb: 2 }}
        options={driverOptions}
        value={selectedDriver}
        onChange={(_, val) => handleSelectDriver(val)}
        getOptionLabel={(opt) => opt.driver_name || ''}
        isOptionEqualToValue={(opt, val) => opt.driver_id === val.driver_id}
        renderInput={(params) => <TextField {...params} label="ドライバーを検索・選択" />}
        noOptionsText="ドライバーがいません"
      />
    );

    if (attendanceLoading) {
      return (
        <Box>
          {picker}
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        </Box>
      );
    }

    if (attendanceError) {
      return (
        <Box>
          {picker}
          <Typography color="error">{attendanceError}</Typography>
        </Box>
      );
    }

    if (!selectedDriver || !driverAttendance) {
      return (
        <Box>
          {picker}
          <Typography color="text.secondary">
            ドライバーを選択すると出勤履歴を表示します
          </Typography>
        </Box>
      );
    }

    // 1: 日別オン時間（棒グラフ）
    if (chartTab === 1) {
      const daily = driverAttendance.daily || [];
      return (
        <Box>
          {picker}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {driverAttendance.driver_name} の日別オン時間（合計:{' '}
            {driverAttendance.summary?.total_hours ?? 0}時間 / 稼働{driverAttendance.summary?.total_days ?? 0}日）
          </Typography>
          {daily.length === 0 ? (
            <Typography>データがありません</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={daily} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip formatter={(v: any) => `${Number(v || 0).toLocaleString()}時間`} />
                <Legend />
                <Bar dataKey="hours" name="オン時間" fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      );
    }

    // 0: 履歴（オン/オフ ＋ 日別オン時間の表）
    const history = driverAttendance.history || [];
    const daily = driverAttendance.daily || [];
    return (
      <Box>
        {picker}
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {driverAttendance.driver_name}　合計オン時間: {driverAttendance.summary?.total_hours ?? 0}時間
          （稼働{driverAttendance.summary?.total_days ?? 0}日）
        </Typography>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>日別オン時間</Typography>
        {daily.length === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 2 }}>データがありません</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto', mb: 2 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>日付</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>オン時間</th>
                </tr>
              </thead>
              <tbody>
                {daily.map((d: any, i: number) => (
                  <tr key={i}>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{d.date}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{d.hours}時間</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>オン/オフ履歴</Typography>
        {history.length === 0 ? (
          <Typography color="text.secondary">履歴がありません</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>日付</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>時刻</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i}>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{h.date}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{h.time}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', color: h.action === 'on' ? '#2e7d32' : '#d32f2f' }}>
                      {h.action === 'on' ? 'オン（共有開始）' : 'オフ（共有終了）'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Box>
    );
  };

  // 売上レポートの表
  const renderSalesTable = () => {
    if (!reportData) return null;
    return (
          <Box>
            {reportData.summary?.total_grand_total !== undefined && (
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                合計総額: {reportData.summary.total_grand_total.toLocaleString()}円
              </Typography>
            )}
            {reportData.summary?.cast_revenue !== undefined && (
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                キャスト収益（キャストバック合計）: {reportData.summary.cast_revenue.toLocaleString()}円
              </Typography>
            )}
            {reportData.summary?.store_revenue !== undefined && (
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                店舗収益（総額-キャストバック）: {reportData.summary.store_revenue.toLocaleString()}円
              </Typography>
            )}

            {/* オプションの種類と件数 */}
            {reportData.summary?.options && reportData.summary.options.length > 0 && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  オプション（件数）
                  {reportData.summary.options_total_count !== undefined
                    ? `　合計: ${reportData.summary.options_total_count.toLocaleString()}件`
                    : ''}
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {reportData.summary.options.map((opt, i) => (
                    <Typography key={i} variant="body2" color="text.secondary">
                      ・{opt.name}: {opt.count.toLocaleString()}件
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {reportData.data && reportData.data.length > 0 ? (
              <Box sx={{ mt: 2, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>日付</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>総額</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>キャスト収益</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>店舗収益</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>注文数</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.data.map((item, index) => (
                      <tr key={index}>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.date}</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.grand_total?.toLocaleString() || 0}円</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.cast_revenue?.toLocaleString() || 0}円</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.store_revenue?.toLocaleString() || 0}円</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.orders || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            ) : (
              <Typography>データがありません</Typography>
            )}
          </Box>
        );
  };

  // キャスト別レポートの表
  const renderCastTable = () => {
    if (!reportData) return null;
    return (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              完了オーダー総数: {reportData.summary?.total_orders || 0}
            </Typography>
            {reportData.summary?.total_castback !== undefined && (
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                キャストバック合計: {reportData.summary.total_castback.toLocaleString()}円
              </Typography>
            )}
            {reportData.summary?.total_grand_total !== undefined && (
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                総額合計: {reportData.summary.total_grand_total.toLocaleString()}円
              </Typography>
            )}
            {reportData.data && reportData.data.length > 0 ? (
              <Box sx={{ mt: 2, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>キャスト名</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>完了オーダー数</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>キャストバック合計</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>総額</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>明細</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.data.map((item, index) => (
                      <tr key={index}>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.cast_name}</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.total_orders || 0}</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.total_castback?.toLocaleString() || 0}円</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.total_grand?.toLocaleString() || 0}円</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openCastDetail(item.cast_id)}
                          >
                            詳細
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            ) : (
              <Typography>データがありません</Typography>
            )}
          </Box>
        );
  };

  const renderCastDetailDialog = () => (
    <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>キャスト別明細{detailData?.cast_name ? `：${detailData.cast_name}` : ''}</span>
        <Box>
          <IconButton onClick={handlePrintDetail} disabled={!detailData} title="印刷">
            <PrintIcon />
          </IconButton>
          <IconButton onClick={() => setDetailOpen(false)} title="閉じる">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {detailLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {detailError && <Typography color="error">{detailError}</Typography>}
        {!detailLoading && !detailError && detailData && (
          <Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              期間：{detailData.start_date} ～ {detailData.end_date} / 期間合計バック：
              {Number(detailData.summary?.total_castback || 0).toLocaleString()}円（完了オーダー
              {detailData.summary?.total_orders || 0}件）
            </Typography>
            {detailData.days && detailData.days.length > 0 ? (
              detailData.days.map((day: any, di: number) => (
                <Box key={di} sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ bgcolor: '#eef3ff', px: 1, py: 0.5, borderRadius: 1 }}
                  >
                    {day.date}（合計バック: {Number(day.total_castback || 0).toLocaleString()}円 /{' '}
                    {day.order_count}件）
                  </Typography>
                  <Box sx={{ pl: 1, mt: 0.5 }}>
                    {day.orders.map((o: any, oi: number) => (
                      <Box
                        key={oi}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          py: 0.5,
                          borderBottom: '1px dashed #ddd',
                        }}
                      >
                        <Typography variant="body2">
                          {o.time} {o.course_label} {Number(o.course_price || 0).toLocaleString()}円
                          {o.options && o.options.length > 0 && (
                            <>
                              （
                              {o.options
                                .map(
                                  (op: any) =>
                                    `${op.name} ${Number(op.price || 0).toLocaleString()}円`
                                )
                                .join('、')}
                              ）
                            </>
                          )}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', ml: 1 }}>
                          バック {Number(o.castback || 0).toLocaleString()}円
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))
            ) : (
              <Typography>該当期間のデータがありません</Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button startIcon={<PrintIcon />} onClick={handlePrintDetail} disabled={!detailData}>
          印刷
        </Button>
        <Button onClick={() => setDetailOpen(false)}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <DashboardLayout title="レポート管理">
      <PageHeader 
        title="レポート管理"
        subtitle="売上やキャストに関する各種レポートを生成します"
      />
      {renderContent()}
      {renderCastDetailDialog()}
    </DashboardLayout>
  );
};

export default Reports;
